/**
 * API Docs & Keys — super-admin only.
 *
 * Two tabs:
 *   • Endpoints — every /api/* route with method, path, summary,
 *     params, grouped by tag, searchable.
 *   • API Keys — every env-var credential + known Mongo-stored
 *     credential, plaintext (super-admin only).
 *
 * Every load is audit-logged on the backend (admin_ops group).
 */
import { useEffect, useMemo, useState } from "react";
import {
  Book,
  Key,
  MagnifyingGlass,
  Copy,
  ShieldWarning,
  CaretRight,
  CaretDown,
  ArrowsClockwise,
  CheckCircle,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import AdminPageHeader from "@/components/AdminPageHeader";
import { fetchApiDocsEndpoints, fetchApiDocsKeys } from "@/lib/api";

const METHOD_COLOR = {
  GET:    "bg-blue-100 text-blue-800 border-blue-200",
  POST:   "bg-emerald-100 text-emerald-800 border-emerald-200",
  PUT:    "bg-amber-100 text-amber-800 border-amber-200",
  PATCH:  "bg-amber-100 text-amber-800 border-amber-200",
  DELETE: "bg-red-100 text-red-800 border-red-200",
};

async function _copy(text) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Copied");
  } catch {
    toast.error("Copy failed");
  }
}

function MethodPill({ m }) {
  return (
    <span className={`inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border rounded-sm font-mono ${METHOD_COLOR[m] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
      {m}
    </span>
  );
}

function EndpointsPanel() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({}); // tag → bool
  const [busy, setBusy] = useState(false);

  async function load() {
    setBusy(true);
    try {
      const d = await fetchApiDocsEndpoints();
      setData(d);
      // Auto-expand the first 5 tags so the page isn't empty on first paint.
      const initial = {};
      (d.tags || []).slice(0, 5).forEach((t) => { initial[t] = true; });
      setExpanded(initial);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load endpoints");
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!data) return {};
    const needle = q.trim().toLowerCase();
    if (!needle) return data.by_tag;
    const out = {};
    Object.entries(data.by_tag).forEach(([tag, rows]) => {
      const kept = rows.filter((r) =>
        r.path.toLowerCase().includes(needle) ||
        (r.summary || "").toLowerCase().includes(needle) ||
        r.methods.some((m) => m.toLowerCase().includes(needle)),
      );
      if (kept.length || tag.toLowerCase().includes(needle)) {
        out[tag] = kept.length ? kept : rows;
      }
    });
    return out;
  }, [data, q]);

  const totalShown = useMemo(
    () => Object.values(filtered).reduce((a, b) => a + b.length, 0),
    [filtered],
  );

  return (
    <div data-testid="endpoints-panel" className="space-y-3">
      <div className="bg-white border border-slate-200 rounded-sm p-2 flex items-center gap-2">
        <MagnifyingGlass size={14} weight="bold" className="text-slate-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="endpoints-search"
          placeholder="Search by path, method, or summary…"
          className="flex-1 text-sm bg-transparent outline-none font-mono"
        />
        <button
          onClick={() => {
            const all = {};
            Object.keys(data?.by_tag || {}).forEach((t) => { all[t] = true; });
            setExpanded(all);
          }}
          className="text-[10px] uppercase font-bold px-2 py-1 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700">
          Expand all
        </button>
        <button
          onClick={() => setExpanded({})}
          className="text-[10px] uppercase font-bold px-2 py-1 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700">
          Collapse all
        </button>
        <button onClick={load} disabled={busy}
                className="text-[10px] uppercase font-bold px-2 py-1 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1">
          <ArrowsClockwise size={10} weight="bold" />
          {busy ? "…" : "Refresh"}
        </button>
        <span className="text-[11px] text-slate-500">
          {data ? `${totalShown} / ${data.count}` : "…"}
        </span>
      </div>

      {Object.entries(filtered).map(([tag, rows]) => {
        const open = !!expanded[tag];
        return (
          <div key={tag} className="bg-white border border-slate-200 rounded-sm" data-testid={`tag-${tag}`}>
            <button
              onClick={() => setExpanded({ ...expanded, [tag]: !open })}
              className="w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-slate-50">
              {open ? <CaretDown size={12} /> : <CaretRight size={12} />}
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700 flex-1">{tag}</span>
              <span className="text-[10px] text-slate-500 font-mono">{rows.length}</span>
            </button>
            {open && (
              <ul className="divide-y divide-slate-100 border-t border-slate-100">
                {rows.map((r) => (
                  <li key={r.path + r.methods.join(",")} className="px-3 py-2 hover:bg-slate-50">
                    <div className="flex items-baseline gap-2">
                      <div className="flex items-center gap-1 shrink-0">
                        {r.methods.map((m) => <MethodPill key={m} m={m} />)}
                      </div>
                      <code className="text-[12px] font-mono text-slate-800 break-all flex-1">
                        {r.path}
                      </code>
                      <button onClick={() => _copy(r.path)}
                              title="Copy path"
                              className="opacity-50 hover:opacity-100 text-slate-500">
                        <Copy size={12} />
                      </button>
                    </div>
                    {(r.summary || r.description) && (
                      <div className="mt-0.5 text-[11px] text-slate-600 ml-1 break-words">
                        {r.summary || r.description}
                      </div>
                    )}
                    {(r.path_params.length > 0 || r.query_params.length > 0) && (
                      <div className="mt-0.5 ml-1 text-[10px] font-mono text-slate-400">
                        {r.path_params.length > 0 && (
                          <>path: {r.path_params.join(", ")}</>
                        )}
                        {r.path_params.length > 0 && r.query_params.length > 0 && " · "}
                        {r.query_params.length > 0 && (
                          <>query: {r.query_params.join(", ")}</>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

function KeyRow({ row }) {
  return (
    <li className="px-3 py-2 flex items-start gap-3" data-testid={`key-row-${row.name}`}>
      <div className="shrink-0 w-40">
        <div className="text-[12px] font-bold text-slate-800 break-all">{row.name}</div>
        <div className="text-[10px] text-slate-500 uppercase tracking-wider">
          {row.kind === "secret" ? (
            <span className="text-red-700 font-bold">secret</span>
          ) : (
            <span>config</span>
          )}
          {" · "}{row.source}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        {row.configured ? (
          <code className="block text-[12px] font-mono bg-slate-50 border border-slate-200 rounded-sm px-2 py-1 break-all text-slate-800">
            {row.value}
          </code>
        ) : (
          <span className="text-[11px] text-slate-400 italic">not configured</span>
        )}
      </div>
      <button
        onClick={() => _copy(row.value || "")}
        disabled={!row.configured}
        className="shrink-0 opacity-60 hover:opacity-100 text-slate-500 disabled:opacity-20"
        title="Copy value">
        <Copy size={14} />
      </button>
    </li>
  );
}

function MongoRow({ row }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="px-3 py-2" data-testid={`mongo-row-${row.collection}`}>
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-40">
          <div className="text-[12px] font-bold text-slate-800">{row.name}</div>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider font-mono">
            mongo · {row.collection}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {row.configured ? (
              <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
                <CheckCircle size={11} weight="bold" /> configured
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                <X size={11} weight="bold" /> not configured
              </span>
            )}
            {row.configured && (
              <button onClick={() => setOpen(!open)}
                      className="text-[10px] uppercase font-bold text-blue-700 hover:text-blue-900">
                {open ? "Hide" : "Reveal"} value
              </button>
            )}
          </div>
          {row.describe && (
            <div className="mt-0.5 text-[11px] text-slate-500">{row.describe}</div>
          )}
          {open && row.configured && (
            <pre className="mt-1 text-[11px] font-mono bg-slate-900 text-slate-100 rounded-sm p-2 overflow-x-auto max-h-64">
{JSON.stringify(row.value, null, 2)}
            </pre>
          )}
        </div>
      </div>
    </li>
  );
}

function KeysPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    setBusy(true);
    try {
      const d = await fetchApiDocsKeys();
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load keys");
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => { load(); }, []);

  const secretRows = (data?.env || []).filter((r) => r.kind === "secret");
  const configRows = (data?.env || []).filter((r) => r.kind === "config");

  return (
    <div data-testid="keys-panel" className="space-y-3">
      <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-[12px] text-red-900 leading-snug">
        <div className="flex items-start gap-2">
          <ShieldWarning size={14} weight="bold" className="mt-0.5 text-red-700" />
          <div className="flex-1">
            <b>Plaintext credentials.</b> Anyone watching your screen
            (or with screenshot access) will see them in full. Don&apos;t
            paste this URL into a chat or screen-share unless you&apos;ve
            confirmed who&apos;s on the call. Every load of this tab is
            written to the ActionLog under the <code>admin_ops</code> group.
          </div>
          <button onClick={load} disabled={busy}
                  className="shrink-0 text-[10px] uppercase font-bold px-2 py-1 rounded-sm bg-white hover:bg-red-100 text-red-800 border border-red-200 inline-flex items-center gap-1">
            <ArrowsClockwise size={10} weight="bold" />
            {busy ? "…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="px-3 py-2 border-b border-slate-200 flex items-center gap-2">
          <Key size={14} weight="bold" className="text-red-700" />
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex-1">
            Secrets (env)
          </h2>
          <span className="text-[10px] text-slate-500 font-mono">{secretRows.length}</span>
        </div>
        <ul className="divide-y divide-slate-100">
          {secretRows.length === 0 && (
            <li className="px-3 py-6 text-center text-[12px] text-slate-500">
              No secret-classified env vars.
            </li>
          )}
          {secretRows.map((r) => <KeyRow key={r.name} row={r} />)}
        </ul>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="px-3 py-2 border-b border-slate-200 flex items-center gap-2">
          <Book size={14} weight="bold" className="text-slate-500" />
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex-1">
            Config (env)
          </h2>
          <span className="text-[10px] text-slate-500 font-mono">{configRows.length}</span>
        </div>
        <ul className="divide-y divide-slate-100">
          {configRows.map((r) => <KeyRow key={r.name} row={r} />)}
        </ul>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="px-3 py-2 border-b border-slate-200 flex items-center gap-2">
          <Key size={14} weight="bold" className="text-purple-700" />
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex-1">
            Mongo-stored integrations
          </h2>
          <span className="text-[10px] text-slate-500 font-mono">{(data?.mongo || []).length}</span>
        </div>
        <ul className="divide-y divide-slate-100">
          {(data?.mongo || []).map((r) => <MongoRow key={r.collection} row={r} />)}
        </ul>
      </div>
    </div>
  );
}

export default function ApiDocsPage({ user, onLogout, onLogoutAll }) {
  const [tab, setTab] = useState("endpoints");

  return (
    <div className="min-h-screen bg-slate-50" data-testid="api-docs-page">
      <AdminPageHeader
        title="API Docs & Keys"
        user={user}
        onLogout={onLogout}
        onLogoutAll={onLogoutAll}
      />
      <main className="max-w-5xl mx-auto px-4 md:px-8 py-6 space-y-4">
        {/* Tabs */}
        <div className="flex items-center gap-2 border-b border-slate-200">
          <button
            data-testid="tab-endpoints"
            onClick={() => setTab("endpoints")}
            className={`px-3 py-2 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px inline-flex items-center gap-1 ${
              tab === "endpoints"
                ? "border-blue-700 text-blue-800"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>
            <Book size={12} weight="bold" />
            Endpoints
          </button>
          <button
            data-testid="tab-keys"
            onClick={() => setTab("keys")}
            className={`px-3 py-2 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px inline-flex items-center gap-1 ${
              tab === "keys"
                ? "border-red-700 text-red-800"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>
            <Key size={12} weight="bold" />
            API Keys
          </button>
        </div>

        {tab === "endpoints" ? <EndpointsPanel /> : <KeysPanel />}
      </main>
    </div>
  );
}
